import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useB2BTranslation } from "../B2BShared/hooks/useB2BTranslation";
import "./B2BTermsPage.css";

const B2BTermsPage = () => {
  const { t } = useB2BTranslation();

  return (
    <div className="b2b-terms-page">
      <div className="terms-header">
        <Link
          to="/gcb"
          className="back-button"
        >
          <ArrowLeft size={20} />
          {t("contact.backToB2B")}
        </Link>
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className="terms-container">
        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the Global Chic Business (GCB) platform, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Membership Eligibility</h2>
            <p>
              Membership in the Global Chic Business community is open to business professionals, entrepreneurs, and organizations that meet our eligibility criteria. You must:
            </p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. Account Registration and Security</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account information, including your password, and for all activities that occur under your account. You agree to:
            </p>
            <ul>
              <li>Immediately notify us of any unauthorized use of your account</li>
              <li>Ensure that you exit from your account at the end of each session</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Update your information to keep it accurate, current, and complete</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. Community Guidelines and Conduct</h2>
            <p>
              As a member of the Global Chic Business community, you agree to:
            </p>
            <ul>
              <li>Respect all members and maintain professional conduct</li>
              <li>Not engage in any fraudulent, deceptive, or illegal activities</li>
              <li>Not spam, harass, or abuse other members</li>
              <li>Not share false, misleading, or defamatory information</li>
              <li>Respect intellectual property rights of others</li>
              <li>Not use the platform for unauthorized commercial purposes</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Business Networking and Partnerships</h2>
            <p>
              The Global Chic Business platform facilitates networking and business opportunities. However:
            </p>
            <ul>
              <li>All business transactions and partnerships are entered into at your own risk</li>
              <li>We do not guarantee the quality, safety, or legality of any business opportunities</li>
              <li>We are not responsible for any disputes between members</li>
              <li>You are responsible for conducting due diligence on potential partners</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>6. Intellectual Property</h2>
            <p>
              All content on the Global Chic Business platform, including but not limited to text, graphics, logos, and software, is the property of Global Chic or its content suppliers and is protected by international copyright and trademark laws. You may not:
            </p>
            <ul>
              <li>Reproduce, distribute, or create derivative works from our content without permission</li>
              <li>Use our trademarks or logos without written consent</li>
              <li>Remove any copyright or proprietary notices from materials</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>7. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the platform, to understand our practices regarding the collection and use of your personal information.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Payment Terms</h2>
            <p>
              If you use any paid services on the platform:
            </p>
            <ul>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>You are responsible for all applicable taxes</li>
              <li>We reserve the right to change our pricing with 30 days' notice</li>
              <li>Payment disputes must be reported within 30 days of the transaction</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>9. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account and access to the platform immediately, without prior notice, for conduct that we believe violates these Terms and Conditions or is harmful to other users, us, or third parties, or for any other reason.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              Global Chic shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the platform. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Global Chic, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of your use of the platform or violation of these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms and Conditions at any time. We will notify members of any material changes via email or through the platform. Your continued use of the platform after such modifications constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Governing Law</h2>
            <p>
              These Terms and Conditions shall be governed by and construed in accordance with applicable international laws. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in the jurisdiction where Global Chic operates.
            </p>
          </section>

          <section className="terms-section">
            <h2>14. Contact Information</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <p className="contact-info">
              <strong>Email:</strong> <a href="mailto:info@globalchic.club">info@globalchic.club</a>
            </p>
          </section>

          <section className="terms-section">
            <h2>15. Entire Agreement</h2>
            <p>
              These Terms and Conditions, together with our Privacy Policy, constitute the entire agreement between you and Global Chic regarding your use of the GCB platform and supersede all prior agreements and understandings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default B2BTermsPage;

